import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

export const ALL_ORGS = 'all' as const;
export type OrgKey = typeof ALL_ORGS | string;

interface Ctx {
  org: OrgKey;
  orgs: string[];        // available orgs (excluding 'all')
  setOrg: (next: OrgKey) => void;
  label: (key: OrgKey) => string;
}

const OrgContext = createContext<Ctx | null>(null);

const LABEL: Record<string, string> = {
  [ALL_ORGS]: 'All orgs',
  '5c-Network':   'Product & Engineering',
  '5c-Analytics': 'Analytics',
  '5c-AI':        'AI',
};

export function OrgProvider({ orgs, children }: { orgs: string[]; children: ReactNode }) {
  const [params, setParams] = useSearchParams();
  const raw = params.get('org') ?? ALL_ORGS;
  const org: OrgKey = raw === ALL_ORGS || orgs.includes(raw) ? raw : ALL_ORGS;

  const setOrg = useCallback((next: OrgKey) => {
    setParams((p) => {
      const merged = new URLSearchParams(p);
      if (next === ALL_ORGS) merged.delete('org');
      else merged.set('org', next);
      return merged;
    }, { replace: true });
  }, [setParams]);

  const label = useCallback((key: OrgKey) => LABEL[key] ?? key, []);

  const value = useMemo<Ctx>(() => ({ org, orgs, setOrg, label }), [org, orgs, setOrg, label]);
  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg() {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used within OrgProvider');
  return ctx;
}
